import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { District, LifeStyleSubCategoryEnum, Region } from 'libs/datalayer/src';
import { from, Subject } from 'rxjs';
import {
    INVALID_STREET_NAME_ERROR_MESSAGE,
    INVALID_STREET_NAME_OR_NUMBER_ERROR_MESSAGE,
    validateNoStreetNameAddedWithNumber,
    validateNoStreetNameOrNumberWithUnitFlat
} from '../../../helpers/address.helper';
import { FormHelperService } from '../../../helpers/form-helper.service';
import { PropertySectionBase } from '../../property-section-base';
import { isEditOrRelistPage } from '../../../helpers/router.helper';
import { ActivatedRoute } from '@angular/router';
import { isDisableInput, validateLandAreaMaxValueByUnit } from '../../../helpers/property-information.helper';
import { takeUntil } from 'rxjs/operators';
import {
    DraftFacade,
    RouterFacade,
    IRouterData
} from '@classifieds-sell/state';
import { ValidatorPipe } from '@classifieds-sell/shared';

@Component({
    selector: 'property-information-section',
    templateUrl: './property-information-section.component.html',
    styleUrls: ['./property-information-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyInformationSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('propertyInformationSectionForm')
    propertyInformationSectionForm: NgForm;

    selectedRegion: Region;
    selectedDistrict: District;

    hasErrorInStreetNumberAndName = false;
    hasErrorInStreetNumberOrNameAndUnitFlat = false;
    invalidStreetNameErrorMessage = INVALID_STREET_NAME_ERROR_MESSAGE;
    invalidStreetNameOrNumberErrorMessage = INVALID_STREET_NAME_OR_NUMBER_ERROR_MESSAGE;
    public isDisabledInput: boolean;
    routerData: IRouterData;
    isDwellingSubcategory: boolean;

    constructor(
        private route: ActivatedRoute,
        public draftFacade: DraftFacade,
        public routerFacade: RouterFacade,
        private changeDetectorRef: ChangeDetectorRef,
        protected formHelperService: FormHelperService,
        private _validatorPipe: ValidatorPipe
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.updateRegions();
            this.setIsDisableInput();
            this.getRouteData();
            this.changeDetectorRef.markForCheck();
        });
    }

    getRouteData(): void {
        from(this.routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((routerStateData) => {
                this.routerData = routerStateData;
                this.isDwellingSubcategory = this.routerData.subCategory === LifeStyleSubCategoryEnum.dwelling;
            });
    }

    isValid(): boolean {
        this.hasErrorInStreetNumberAndName = false;
        this.hasErrorInStreetNumberOrNameAndUnitFlat = false;
        this.formHelperService.validate(this.propertyInformationSectionForm);
        const validationResult = this.validate();
        return this.formValidationWhenDisabled(validationResult, this.propertyInformationSectionForm);
    }

    protected formValidationWhenDisabled(validationResult: boolean, ngForm: NgForm): boolean {

        // if page is  a edit page when startDate > 7 then all inputs are disabled => form should be valid
        const isFormDisabled = ngForm.form.disabled;

        return (((isEditOrRelistPage(this.route) && isFormDisabled)
            || ngForm.form.valid)
            && validationResult);
    }

    dirty(): boolean {
        return this.propertyInformationSectionForm.form.dirty;
    }

    private updateRegions() {
        this.selectedRegion = this.metadata.regions.find(
            (region: Region) =>
                region.regionId === this.draftData.propertyInformation.regionId
        );
        this.selectedDistrict = this.selectedRegion?.districts?.find(
            (district: District) =>
                district.districtId ===
                this.draftData.propertyInformation.districtId
        );
    }

    regionChange() {
        this.selectedDistrict = null;
        this.draftData.propertyInformation.districtId = null;
        this.draftData.propertyInformation.suburbId = null;
        this.updateRegions();
        this.publish();
    }

    districtChange() {
        this.draftData.propertyInformation.suburbId = null;
        this.updateRegions();
        this.publish();
    }

    private validate(): boolean {
        if (validateNoStreetNameOrNumberWithUnitFlat(
            this.draftData.propertyInformation.streetName,
            this.draftData.propertyInformation.streetNumber,
            this.draftData.propertyInformation.unitFlat)) {
            this.hasErrorInStreetNumberOrNameAndUnitFlat = true;
            this.changeDetectorRef.markForCheck();
            return false;
        }

        if (
            validateNoStreetNameAddedWithNumber(
                this.draftData.propertyInformation.streetName,
                this.draftData.propertyInformation.streetNumber
            )
        ) {
            this.hasErrorInStreetNumberAndName = true;
            this.changeDetectorRef.markForCheck();
            return false;
        }

        return true;
    }

    public setIsDisableInput(): void {
        this.isDisabledInput = isDisableInput(this.route, this.metadata);
    }

    public propertyAreaValidation(){
        return validateLandAreaMaxValueByUnit(this.draftData, this.validation, this._validatorPipe);
    }
}
